import React, { useRef, useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  IoPerson,
  IoMailOutline,
  IoLockClosedOutline,
  IoEye,
  IoEyeOff,
} from "react-icons/io5";
import "./AuthForms.css";
import { useI18n } from '../contexts/I18n';
import { useAuth } from '../contexts/AuthContext';
import { getBookingIntent, clearBookingIntent } from '../utils/bookingIntent';

function safeInternalPath(value, fallback = "/dashboard") {
  if (typeof value !== "string") return fallback;
  const path = value.trim();
  if (!path.startsWith("/") || path.startsWith("//")) return fallback;
  if (path.includes("://") || path.includes("\\")) return fallback;
  return path;
}

const PUB = process.env.PUBLIC_URL || "";
/* Local file — S3 welcome URL currently returns 403 in browser */
const WELCOME_VIDEO_SRC = `${PUB}/videos/welcome.mp4`;
const HERO_BRAND_LOGO = `${PUB}/images/ehgezly-hero-logo.png?v=4`;

export default function Login() {
  const { t, lang } = useI18n();
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const [form, setForm] = useState({ email: "", password: "", rememberMe: false });
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const from = safeInternalPath(redirectParam || location.state?.from?.pathname, "/dashboard");
  const hasBookingIntent = location.state?.bookingIntent || false;
  const isRtl = lang === "ar";

  const inflight = useRef(null);
  useEffect(() => () => inflight.current?.abort(), []);

  useEffect(() => {
    if (location.state?.verified) {
      setOk(t("emailVerified") || "Email verified successfully!");
    }
    if (location.state?.email) {
      setForm((f) => ({ ...f, email: location.state.email }));
    }
  }, [location.state, t]);

  useEffect(() => {
    if (!authLoading && isAuthenticated && redirectParam) {
      navigate(from, { replace: true });
    }
  }, [authLoading, isAuthenticated, redirectParam, from, navigate]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      el.pause();
      return undefined;
    }
    el.muted = true;
    const play = () => {
      const p = el.play();
      if (p?.catch) p.catch(() => {});
    };
    play();
    el.addEventListener("loadeddata", play);
    return () => el.removeEventListener("loadeddata", play);
  }, []);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    if (error || ok) { setError(""); setOk(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setOk("");
    if (!form.email || !form.password) return setError(t('emailRequired'));
    setLoading(true);

    try {
      inflight.current?.abort();
      inflight.current = new AbortController();

      const result = await login(form.email, form.password, form.rememberMe);

      if (result.success) {
        setOk(t('welcomeBack'));

        const bookingIntent = getBookingIntent();
        if (bookingIntent && hasBookingIntent) {
          clearBookingIntent();
          navigate('/dashboard', {
            state: {
              bookingIntent: bookingIntent,
              fromBooking: true
            }
          });
        } else if (result.emailVerified === false) {
          navigate('/dashboard', { replace: true, state: { openProfile: true } });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={`auth-page auth-page--split auth-page--login${isRtl ? " rtl" : ""}`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <aside
        className="auth-brand auth-brand--showcase"
        aria-label={t("welcomeBack")}
      >
        <div className="auth-brand__video" aria-hidden="true">
          <video
            ref={videoRef}
            className="auth-brand__video-el"
            src={WELCOME_VIDEO_SRC}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />
          <div className="auth-brand__video-veil" />
        </div>
        <div className="auth-brand__hero-mark">
          <div className="auth-brand__hero-logo-wrap">
            <img
              src={HERO_BRAND_LOGO}
              alt=""
              className="auth-brand__hero-logo"
              draggable="false"
            />
          </div>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-form-panel">
          <header className="auth-form-head">
            <div className="auth-form-head__mark" aria-hidden="true">
              <span className="auth-avatar">
                <IoPerson className="auth-avatar__icon" />
              </span>
            </div>
            <h2>{t("welcomeBack")}</h2>
            <p>{t("signInSubtitle")}</p>
          </header>

          {error && <div className="error-message" role="alert">{error}</div>}
          {ok && <div className="success-message" role="status">{ok}</div>}

          <form className="auth-form" onSubmit={submit} aria-busy={loading} noValidate>
            <div className="form-group">
              <label className="form-label form-label-with-icon" htmlFor="login-email">
                <span className="form-label-icon" aria-hidden="true">
                  <IoMailOutline className="form-label-icon-svg" />
                </span>
                <span className="form-label-text">{t("emailAddress")}</span>
              </label>
              <input
                id="login-email"
                type="email"
                name="email"
                placeholder={t("emailPlaceholder")}
                value={form.email}
                onChange={change}
                autoComplete="email"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label form-label-with-icon" htmlFor="login-password">
                <span className="form-label-icon" aria-hidden="true">
                  <IoLockClosedOutline className="form-label-icon-svg" />
                </span>
                <span className="form-label-text">{t("password")}</span>
              </label>
              <div className="auth-password-field">
                <input
                  id="login-password"
                  type={showPwd ? "text" : "password"}
                  name="password"
                  placeholder={t("passwordPlaceholder")}
                  value={form.password}
                  onChange={change}
                  autoComplete="current-password"
                  className="form-input"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPwd((s) => !s)}
                  aria-pressed={showPwd}
                  aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                >
                  {showPwd ? <IoEye /> : <IoEyeOff />}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={form.rememberMe}
                  onChange={change}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                {t("rememberMe")}
              </label>
              <NavLink to="/forgot-password" className="forgot-password-link">
                {t("forgotPasswordQ")}
              </NavLink>
            </div>

            <button type="submit" className="auth-button" disabled={loading}>
              <span className="button-content">
                {loading ? <span className="loading-spinner"></span> : t("signIn")}
              </span>
            </button>
          </form>

          <div className="auth-footer">
            <p>
              {t("dontHaveAccount")}{" "}
              <NavLink to="/register" className="auth-link">
                {t("signUpHere")}
              </NavLink>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
